export const TEST_LANGUAGES = [
  {id: 'ru', label: 'Russian'},
  {id: 'en', label: 'English'},
];

export const REQUIRED_QUESTION_COUNT = 3;

export const QUIZ_BY_LANGUAGE = {
  ru: [
    {
      question: 'Когда будет письменный экзамен?',
      options: [
        {
          // Previous wave: 'Когда-то в мае'.
          text: 'В любое удобное время',
          correct: false,
          answer: 'Нет, письменный экзамен проходит в назначенное время.',
        },
        {
          // Previous wave: 'Сегодня, 3-го мая в 09:00 UTC'.
          text: 'В дату и время, которые сообщат кандидатам',
          correct: true,
          answer: 'Да! Дату и время экзамена сообщат кандидатам заранее.',
        },
      ],
    },
    {
      question: 'Что нужно для старта?',
      options: [
        {
          text: 'Я могу написать на планшете.',
          correct: false,
          answer: 'Нет, нужен комьютер и смартфон!',
        },
        {
          text: 'Нужен хороший компьютер.',
          correct: false,
          answer: 'Нет, этого не достаточно!',
        },
        {
          text: 'Хороший интернет, ноутбук на MacOs или Windows с камерой и смартфон',
          correct: true,
          answer: 'Точно! Без них не обойтись.',
        },
      ],
    },
    {
      question: 'Зарядка к ноутбуку и смартфону нужна?',
      options: [
        {
          text: 'Очень желательно! Если у вас разрядится ноутбук или смартфон, то запись точно будет отсутствовать. В этом случае ваш экзамен может быть аннулирован.',
          correct: true,
          answer: 'Да-да, лучше перестраховаться.',
        },
        {
          text: 'Если смартфон разрядится, я возьму другой',
          correct: false,
          answer: 'Если это займёт много времени, или будет выглядеть подозрительно на записи, то экзамен может быть аннулирован',
        },
      ],
    },
  ],
  en: [
    {
      question: 'When will the written exam be?',
      options: [
        {
          // Previous wave: 'Sometime in May'.
          text: 'At any convenient time',
          correct: false,
          answer: 'No, the written exam takes place at a scheduled time.',
        },
        {
          // Previous wave: 'Today, May 3rd at 09:00 UTC'.
          text: 'On the date and at the time communicated to candidates',
          correct: true,
          answer: 'Yes! Candidates will be informed of the exam date and time in advance.',
        },
      ],
    },
    {
      question: 'What is needed to start?',
      options: [
        {
          text: 'I can write on a tablet.',
          correct: false,
          answer: 'No, a computer and a smartphone are needed!',
        },
        {
          text: 'A good computer is needed.',
          correct: false,
          answer: "No, that's not enough!",
        },
        {
          text: 'Good internet, a MacOS or Windows laptop with a camera, and a smartphone',
          correct: true,
          answer: "Exactly! You can't do without them.",
        },
      ],
    },
    {
      question: 'Do I need a charger for the laptop and smartphone?',
      options: [
        {
          text: 'Highly recommended! If your laptop or smartphone runs out of battery, the recording will definitely be missing. In this case, your exam may be cancelled.',
          correct: true,
          answer: "Yes, it's better to be safe.",
        },
        {
          text: 'If the smartphone runs out of battery, I will take another one',
          correct: false,
          answer: 'If this takes a lot of time, or looks suspicious on the recording, the exam may be cancelled.',
        },
      ],
    },
  ],
};

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function validateQuizConfig(languages = TEST_LANGUAGES, quizByLanguage = QUIZ_BY_LANGUAGE, expectedQuestionCount = REQUIRED_QUESTION_COUNT) {
  const errors = [];

  if (!Array.isArray(languages) || languages.length === 0) {
    return {ok: false, errors: ['At least one test language must be configured.'], expectedQuestionCount: 0};
  }

  const ids = new Set();
  for (const lang of languages) {
    if (!hasText(lang?.id)) {
      errors.push('Every configured language must have a non-empty id.');
      continue;
    }
    if (!hasText(lang?.label)) {
      errors.push(`Language "${lang.id}" must have a non-empty label.`);
    }
    if (ids.has(lang.id)) {
      errors.push(`Language "${lang.id}" is configured more than once.`);
    }
    ids.add(lang.id);
  }

  if (expectedQuestionCount === 0) {
    errors.push('Configured languages must include at least one question.');
  }

  for (const lang of languages) {
    if (!hasText(lang?.id)) {
      continue;
    }

    const questions = quizByLanguage?.[lang.id];
    if (!Array.isArray(questions)) {
      errors.push(`Language "${lang.id}" is configured but has no quiz data.`);
      continue;
    }

    if (questions.length !== expectedQuestionCount) {
      errors.push(`Language "${lang.id}" must have ${expectedQuestionCount} questions, got ${questions.length}.`);
    }

    questions.forEach((question, questionIndex) => {
      const label = `${lang.id} question ${questionIndex + 1}`;
      if (!hasText(question?.question)) {
        errors.push(`${label} must have non-empty question text.`);
      }
      if (!Array.isArray(question?.options)) {
        errors.push(`${label} must have options.`);
        return;
      }
      if (question.options.length < 2 || question.options.length > 4) {
        errors.push(`${label} must have 2-4 options.`);
      }

      let correctCount = 0;
      question.options.forEach((option, optionIndex) => {
        if (!hasText(option?.text)) {
          errors.push(`${label} option ${optionIndex + 1} must have non-empty text.`);
        }
        if (!hasText(option?.answer)) {
          errors.push(`${label} option ${optionIndex + 1} must have non-empty feedback.`);
        }
        if (option?.correct === true) {
          correctCount += 1;
        }
      });

      if (correctCount === 0) {
        errors.push(`${label} must have at least one correct option.`);
      }
    });
  }

  return {ok: errors.length === 0, errors, expectedQuestionCount};
}

export function getInitialQuizState(languageId) {
  return {
    languageId,
    questionIndex: 0,
    completed: false,
  };
}

export function getCurrentQuestion(state, quizByLanguage = QUIZ_BY_LANGUAGE) {
  if (!state || state.completed) {
    return null;
  }
  return quizByLanguage?.[state.languageId]?.[state.questionIndex] || null;
}

export function applyQuizAnswer(state, optionIndex, quizByLanguage = QUIZ_BY_LANGUAGE) {
  const question = getCurrentQuestion(state, quizByLanguage);
  if (!question) {
    throw new Error('Cannot answer quiz: current question was not found.');
  }

  const option = question.options?.[optionIndex];
  if (!option) {
    throw new Error(`Cannot answer quiz: option ${optionIndex} was not found.`);
  }

  const questions = quizByLanguage[state.languageId];
  const nextQuestionIndex = option.correct ? state.questionIndex + 1 : state.questionIndex;
  const completed = option.correct && nextQuestionIndex >= questions.length;

  return {
    option,
    correct: option.correct === true,
    state: {
      ...state,
      questionIndex: nextQuestionIndex,
      completed,
    },
  };
}
